<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject } from "vue"
import { useI18n } from "vue-i18n"
import { useToast } from "primevue/usetoast"
import type { Services } from "@services"
import { useFetchWrapper } from "@core/composables"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useSourceStore } from "@core/stores/source.store"

// ================================
// PROPS & EMITS
// ================================

const props = defineProps<{
    /** Identifiant Mongo de la source */
    sourceId: string
    /** Libellé affiché dans le message (ex. idsource métier) */
    sourceLabel?: string
}>()

const emit = defineEmits<{
    /** Émis après suppression API réussie et mise à jour des stores */
    success: [payload: { sourceId: string }]
}>()

const visible = defineModel<boolean>("visible", { default: false })

// ================================
// COMPOSABLES
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            message:
                "Êtes-vous sûr de vouloir supprimer la source {id} ? Cette action est irréversible.",
            header: "Confirmation de suppression",
            cancel: "Annuler",
            confirm: "Supprimer",
            successSummary: "Suppression réussie",
            successDetail: "La source a été supprimée avec succès",
            errorSummary: "Erreur",
            errorDetail:
                "Une erreur est survenue lors de la suppression de la source",
        },
    },
})

const services = inject<Services>("services")
const sourceStore = useSourceStore()
const buildingStore = useBuildingStore()
const toast = useToast()

const {
    fetch: fetchDeleteSource,
    hasError: hasErrorDeleteSource,
    isFetching: getIsDeletingSource,
} = useFetchWrapper(
    async (kwargs?: { sourceId?: string }) => {
        const sourceId = kwargs?.sourceId
        if (!sourceId) return
        await services!.$deleteSourceById(sourceId)

        const idxStore = sourceStore.sources.findIndex(
            (s) => s._id_source === sourceId
        )
        if (idxStore !== -1) {
            sourceStore.sources.splice(idxStore, 1)
        }

        const b = buildingStore.building
        if (b?.sources?.length) {
            const idxBuilding = b.sources.findIndex(
                (s) => s._id_source === sourceId
            )
            if (idxBuilding !== -1) {
                b.sources.splice(idxBuilding, 1)
            }
        }
    },
    () => {
        toast.add({
            severity: "error",
            summary: t("errorSummary"),
            detail: t("errorDetail"),
            life: 3000,
        })
    }
)

// ================================
// COMPUTED
// ================================

const getMessage = computed(() =>
    t("message", {
        id: props.sourceLabel || props.sourceId,
    })
)

// ================================
// HANDLERS
// ================================

function onVisibleUpdate(v: boolean): void {
    if (!v && getIsDeletingSource.value) return
    visible.value = v
}

function onCancel(): void {
    if (getIsDeletingSource.value) return
    visible.value = false
}

/**
 * Appelle l’API de suppression ; ferme et notifie uniquement en cas de succès.
 */
async function onConfirm(): Promise<void> {
    if (!props.sourceId) return

    await fetchDeleteSource({ sourceId: props.sourceId })

    if (hasErrorDeleteSource.value) return

    visible.value = false

    toast.add({
        severity: "success",
        summary: t("successSummary"),
        detail: t("successDetail"),
        life: 3000,
    })

    emit("success", { sourceId: props.sourceId })
}
</script>

<template>
    <BaseDialog
        :visible="visible"
        modal
        :header="t('header')"
        :closable="!getIsDeletingSource"
        :dismissable-mask="!getIsDeletingSource"
        :close-on-escape="!getIsDeletingSource"
        class="max-w-md w-full"
        :breakpoints="{ '575px': '90vw' }"
        @update:visible="onVisibleUpdate"
    >
        <div class="flex gap-3 items-start">
            <span
                class="pi pi-info-circle text-primary text-xl shrink-0 mt-0.5"
                aria-hidden="true"
            />
            <p class="text-surface-700 m-0 leading-relaxed">
                {{ getMessage }}
            </p>
        </div>
        <template #footer>
            <div class="flex justify-end gap-2 w-full">
                <BaseButton
                    :label="t('cancel')"
                    severity="secondary"
                    outlined
                    :disabled="getIsDeletingSource"
                    @click="onCancel"
                />
                <BaseButton
                    :label="t('confirm')"
                    icon="pi pi-trash"
                    severity="danger"
                    :loading="getIsDeletingSource"
                    :disabled="getIsDeletingSource"
                    @click="onConfirm"
                />
            </div>
        </template>
    </BaseDialog>
</template>
